import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CheckCircle2, Loader2, Copy, Check, Smartphone, Upload } from "lucide-react";
import { toast } from "sonner";
import { submitOrder, uploadProof, formatPHP, resolveImg } from "@/lib/api";
import { ComplianceNote, DEFAULT_COMPLIANCE_NOTE } from "@/components/ComplianceNote";

const emptyForm = { name: "", email: "", phone: "", city: "", quantity: 1, message: "", gcashReference: "", website: "" };

export const OrderDialog = ({ product, open, onClose, payment = {}, disclaimer = DEFAULT_COMPLIANCE_NOTE }) => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [copied, setCopied] = useState(false);
  const [proof, setProof] = useState("");
  const [uploadingProof, setUploadingProof] = useState(false);
  const proofRef = useRef(null);
  const [variantSel, setVariantSel] = useState({});

  const gcash = !!payment.gcashEnabled;
  const variantGroups = product?.variants || [];
  const variantText = Object.entries(variantSel)
    .filter(([, v]) => v)
    .map(([k, v]) => `${k}: ${v}`)
    .join(" \u2022 ");

  useEffect(() => {
    if (open) {
      setForm(emptyForm);
      setErrors({});
      setDone(false);
      setSubmitting(false);
      setCopied(false);
      setProof("");
      setUploadingProof(false);
      setVariantSel({});
    }
  }, [open, product]);

  const qty = Number(form.quantity) || 1;
  const amount = useMemo(() => (Number(product?.pricePHP) || 0) * qty, [product, qty]);

  const set = (k) => (ev) => setForm((s) => ({ ...s, [k]: ev.target.value }));

  const validate = (f) => {
    const e = {};
    if (!f.name || f.name.trim().length < 2) e.name = "Pakilagay ang pangalan mo";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email)) e.email = "Maglagay ng valid na email";
    if (!f.phone || f.phone.trim().length < 7) e.phone = "Maglagay ng valid na numero";
    if (gcash && (!f.gcashReference || f.gcashReference.trim().length < 6)) e.gcashReference = "Ilagay ang GCash Reference No. pagkatapos magbayad";
    variantGroups.forEach((g) => {
      if (!variantSel[g.name]) e[`variant_${g.name}`] = `Pumili ng ${g.name.toLowerCase()}`;
    });
    return e;
  };

  const copyNumber = async () => {
    try {
      await navigator.clipboard.writeText(payment.gcashNumber || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("Clipboard copy failed:", err);
    }
  };

  const handleProof = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Image files lang po");
      return;
    }
    setUploadingProof(true);
    try {
      const r = await uploadProof(f);
      setProof(r.url);
      toast.success("Na-attach ang screenshot");
    } catch (err) {
      toast.error("Hindi na-upload ang screenshot");
    } finally {
      setUploadingProof(false);
      if (proofRef.current) proofRef.current.value = "";
    }
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const eobj = validate(form);
    setErrors(eobj);
    if (Object.keys(eobj).length) return;
    setSubmitting(true);
    try {
      await submitOrder({
        productName: product?.name || "",
        productId: product?.slug || "",
        name: form.name,
        email: form.email,
        phone: form.phone,
        city: form.city,
        quantity: qty,
        message: form.message,
        paymentMethod: gcash ? "gcash" : "inquiry",
        gcashReference: form.gcashReference,
        amount,
        proofImage: proof,
        variants: variantSel,
        website: form.website,
      });
      setDone(true);
      toast.success(gcash ? "Na-receive ang order + GCash details mo!" : "Na-send ang inquiry! Kokontakin kita agad.");
    } catch (err) {
      toast.error("May nangyaring mali. Pakisubukang muli.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto rounded-2xl" data-testid="order-dialog">
        {done ? (
          <div className="flex flex-col items-center py-6 text-center">
            <CheckCircle2 className="h-14 w-14 text-primary" />
            <h3 className="font-display mt-4 text-2xl font-semibold text-foreground">
              {gcash ? "Salamat sa iyong bayad!" : "Na-receive ang inquiry!"}
            </h3>
            <p className="mt-2 max-w-sm text-sm text-muted-foreground">
              {gcash ? (
                <>Na-receive namin ang order mo para sa <span className="font-medium text-foreground">{product?.name}</span> ({formatPHP(amount)}). Ico-confirm namin ang GCash payment mo (Ref: <span className="font-medium text-foreground">{form.gcashReference}</span>) bago i-deliver.</>
              ) : (
                <>Salamat{form.name ? `, ${form.name.split(" ")[0]}` : ""}! Kokontakin kita para i-confirm ang order mo para sa <span className="font-medium text-foreground">{product?.name}</span>, kasama ang presyo at delivery.</>
              )}
            </p>
            {variantText ? (
              <p className="mt-2 text-sm font-medium text-foreground" data-testid="order-success-variants">{variantText}</p>
            ) : null}
            <Button className="mt-6" onClick={onClose} data-testid="order-success-close">Tapos na</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">{gcash ? "Bumili via GCash" : "I-order / Mag-inquire"}</DialogTitle>
              <DialogDescription>
                {product?.name ? (
                  <><span className="font-medium text-foreground">{product.name}</span>{amount ? <> — {formatPHP(amount)}</> : null}</>
                ) : (
                  <>Punan ang details mo at babalikan kita agad.</>
                )}
              </DialogDescription>
            </DialogHeader>

            {gcash ? (
              <div className="rounded-xl border border-border bg-secondary/50 p-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                  <Smartphone className="h-4 w-4 text-primary" /> Bayaran via GCash
                </div>
                <div className="mt-3 flex items-start gap-4">
                  {payment.gcashQr ? (
                    <div className="h-28 w-28 shrink-0 overflow-hidden rounded-lg border border-border bg-white p-1">
                      <img src={resolveImg(payment.gcashQr)} alt="GCash QR" className="h-full w-full object-contain" />
                    </div>
                  ) : null}
                  <div className="min-w-0 flex-1 text-sm">
                    <p className="text-muted-foreground">Ipadala ang <span className="font-semibold text-foreground">{formatPHP(amount)}</span> kay:</p>
                    {payment.gcashName ? <p className="mt-1 font-semibold text-foreground">{payment.gcashName}</p> : null}
                    <div className="mt-1 flex items-center gap-2">
                      <span className="font-mono text-base font-semibold text-foreground" data-testid="gcash-number">{payment.gcashNumber || "-"}</span>
                      <Button type="button" size="sm" variant="outline" className="h-7 px-2" onClick={copyNumber} data-testid="gcash-copy-number">
                        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                      </Button>
                    </div>
                  </div>
                </div>
                {payment.instructions ? (
                  <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{payment.instructions}</p>
                ) : null}
              </div>
            ) : null}

            <form onSubmit={handleSubmit} className="mt-2 space-y-4" noValidate>
              <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />

              {variantGroups.length > 0 && (
                <div className="rounded-xl border border-border bg-secondary/40 p-3" data-testid="order-variants">
                  <p className="text-sm font-semibold text-foreground">Piliin ang options</p>
                  <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {variantGroups.map((g) => {
                      const key = g.name.toLowerCase();
                      return (
                        <div key={g.name}>
                          <Label htmlFor={`o-variant-${key}`}>{g.name}</Label>
                          <Select value={variantSel[g.name] || ""} onValueChange={(v) => setVariantSel((s) => ({ ...s, [g.name]: v }))}>
                            <SelectTrigger id={`o-variant-${key}`} className="mt-1.5 h-11 bg-white" data-testid={`order-variant-${key}`}>
                              <SelectValue placeholder={`Pumili ng ${g.name.toLowerCase()}`} />
                            </SelectTrigger>
                            <SelectContent>
                              {g.options.map((o) => (
                                <SelectItem key={o} value={o}>{o}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {errors[`variant_${g.name}`] && <p className="mt-1 text-xs text-destructive">{errors[`variant_${g.name}`]}</p>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="o-name">Buong pangalan</Label>
                  <Input id="o-name" className="mt-1.5 h-11 bg-white" value={form.name} onChange={set("name")} placeholder="Juan Dela Cruz" data-testid="order-name-input" />
                  {errors.name && <p className="mt-1 text-xs text-destructive">{errors.name}</p>}
                </div>
                <div>
                  <Label htmlFor="o-phone">Numero</Label>
                  <Input id="o-phone" className="mt-1.5 h-11 bg-white" value={form.phone} onChange={set("phone")} placeholder="+63 9XX XXX XXXX" data-testid="order-phone-input" />
                  {errors.phone && <p className="mt-1 text-xs text-destructive">{errors.phone}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="o-email">Email</Label>
                  <Input id="o-email" type="email" className="mt-1.5 h-11 bg-white" value={form.email} onChange={set("email")} placeholder="ikaw@example.com" data-testid="order-email-input" />
                  {errors.email && <p className="mt-1 text-xs text-destructive">{errors.email}</p>}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="o-city">Lungsod</Label>
                    <Input id="o-city" className="mt-1.5 h-11 bg-white" value={form.city} onChange={set("city")} placeholder="Maynila" data-testid="order-city-input" />
                  </div>
                  <div>
                    <Label htmlFor="o-qty">Dami</Label>
                    <Input id="o-qty" type="number" min={1} className="mt-1.5 h-11 bg-white" value={form.quantity} onChange={set("quantity")} data-testid="order-qty-input" />
                  </div>
                </div>
              </div>

              {gcash ? (
                <div>
                  <Label htmlFor="o-ref">GCash Reference No.</Label>
                  <Input id="o-ref" className="mt-1.5 h-11 bg-white" value={form.gcashReference} onChange={set("gcashReference")} placeholder="hal. 1234 567 890123" data-testid="order-gcash-ref-input" />
                  {errors.gcashReference && <p className="mt-1 text-xs text-destructive">{errors.gcashReference}</p>}
                  <div className="mt-3">
                    <Label>GCash screenshot (optional)</Label>
                    <div className="mt-1.5 flex items-center gap-3">
                      <Button type="button" variant="outline" size="sm" onClick={() => proofRef.current?.click()} disabled={uploadingProof} data-testid="order-proof-upload">
                        {uploadingProof ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Ina-upload...</> : <><Upload className="mr-1.5 h-3.5 w-3.5" /> Mag-attach ng resibo</>}
                      </Button>
                      {proof ? (
                        <span className="flex items-center gap-1 text-xs font-medium text-primary"><Check className="h-3.5 w-3.5" /> Naka-attach</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">Patunay ng bayad</span>
                      )}
                      <input ref={proofRef} type="file" accept="image/*" className="hidden" onChange={handleProof} data-testid="order-proof-input" />
                    </div>
                    {proof ? <img src={resolveImg(proof)} alt="proof" className="mt-2 h-20 rounded-lg border border-border object-cover" /> : null}
                  </div>
                </div>
              ) : null}

              <div>
                <Label htmlFor="o-msg">Mensahe (optional)</Label>
                <Textarea id="o-msg" className="mt-1.5 min-h-20 bg-white" value={form.message} onChange={set("message")} placeholder="May tanong o preference ka ba?" data-testid="order-message-input" />
              </div>

              <Button type="submit" className="h-11 w-full bg-accent text-accent-foreground hover:bg-accent/90" disabled={submitting} data-testid="order-submit-button">
                {submitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sina-send...</>) : (gcash ? `I-confirm ang order (${formatPHP(amount)})` : "I-send ang inquiry")}
              </Button>
              <p className="text-center text-xs text-muted-foreground">
                {gcash ? "I-verify namin ang bayad mo bago i-deliver. Walang spam." : "Walang spam — ang details mo ay gagamitin lang para sa inquiry mo."}
              </p>
              <div className="flex justify-center">
                <ComplianceNote text={disclaimer} testid="order-compliance-note" />
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
